import type { ReconFlagSeverity } from '@ovComponents/4-module/reconFlags/utils/reconFlags.types';
import { parseReconFlagsSeverityTab } from '@ovComponents/4-module/reconFlags/utils/reconFlagsGridVariables';
import { usePageState } from '@utils/usePageState';
import { useCallback, useMemo, useState } from 'react';
import type { FlagGlossaryFiltersCardProps } from '../filters/flagGlossaryFiltersCard.types';
import type { FlagGlossaryFilterValues } from './flagGlossary.types';
import {
  FLAG_GLOSSARY_DEFAULT_FILTER_VALUES,
  hasFlagGlossaryActiveFilters,
} from './flagGlossaryFilterDefaults';
import type { FlagGlossaryCategoryOption } from './flagGlossaryFiltering';

type UseFlagGlossaryFiltersInput = {
  categoryOptions: FlagGlossaryCategoryOption[];
};

export const useFlagGlossaryFilters = ({
  categoryOptions,
}: UseFlagGlossaryFiltersInput): {
  filters: FlagGlossaryFilterValues;
  filtersProps: Omit<FlagGlossaryFiltersCardProps, 'theme'>;
} => {
  const [severityTabStored, setSeverityTabStored] = usePageState(
    FLAG_GLOSSARY_DEFAULT_FILTER_VALUES.severityTab,
    'fgSev',
  );
  const severityTab = useMemo(
    () => parseReconFlagsSeverityTab(severityTabStored),
    [severityTabStored],
  );

  const [categoryFilter, setCategoryFilter] = usePageState(
    FLAG_GLOSSARY_DEFAULT_FILTER_VALUES.categoryFilter,
    'fgCat',
  );
  const [search, setSearch] = useState('');

  const filters = useMemo(
    (): FlagGlossaryFilterValues => ({
      severityTab,
      categoryFilter,
      search,
    }),
    [severityTab, categoryFilter, search],
  );

  const onSeverityTabChange = useCallback(
    (tab: 'ALL' | ReconFlagSeverity) => {
      setSeverityTabStored(tab);
    },
    [setSeverityTabStored],
  );

  const onCategoryFilterChange = useCallback(
    (value: string) => {
      setCategoryFilter(value);
    },
    [setCategoryFilter],
  );

  const hasActiveFilters = useMemo(
    () =>
      hasFlagGlossaryActiveFilters({
        severityTab,
        categoryFilter,
        search,
      }),
    [severityTab, categoryFilter, search],
  );

  const onClearFilters = useCallback(() => {
    setSeverityTabStored(FLAG_GLOSSARY_DEFAULT_FILTER_VALUES.severityTab);
    setCategoryFilter(FLAG_GLOSSARY_DEFAULT_FILTER_VALUES.categoryFilter);
    setSearch(FLAG_GLOSSARY_DEFAULT_FILTER_VALUES.search);
  }, [setCategoryFilter, setSeverityTabStored]);

  const filtersProps = useMemo(
    (): Omit<FlagGlossaryFiltersCardProps, 'theme'> => ({
      severityTab,
      onSeverityTabChange,
      categoryFilter,
      onCategoryFilterChange,
      categoryOptions,
      search,
      onSearchChange: setSearch,
      hasActiveFilters,
      onClearFilters,
    }),
    [
      severityTab,
      onSeverityTabChange,
      categoryFilter,
      onCategoryFilterChange,
      categoryOptions,
      search,
      hasActiveFilters,
      onClearFilters,
    ],
  );

  return { filters, filtersProps };
};
